import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaUsers,
  FaBox,
  FaClipboardList,
  FaCheckCircle,
  FaDollarSign,
  FaArrowRight,
  FaShieldAlt,
} from "react-icons/fa";
import RoleIllustration from "../../components/RoleIllustration";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import AdminLayout from "../../components/AdminLayout";
import api from "../../utils/api";
import { getImageUrl } from "../../utils/imageUtils";
import {
  toLocalizedNumber,
  toLocalizedPrice,
  toLocalizedStatus,
} from "../../utils/localeUtils";
import { usePopup } from "../../context/PopupContext";
import { useTranslation } from "react-i18next";
import "./AdminDashboard.css";

const PIE_COLORS = [
  "#22c55e",
  "#3b82f6",
  "#8b5cf6",
  "#06b6d4",
  "#f59e0b",
  "#ef4444",
  "#0d9488",
];

const AdminDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showToast } = usePopup();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalFarmers: 0,
    totalConsumers: 0,
    totalProducts: 0,
    totalOrders: 0,
    activeOrdersToday: 0,
    completedOrders: 0,
    cancelledOrders: 0,
    pendingReviews: 0,
    totalRevenue: 0,
    pendingVerifications: 0,
  });
  const [recentUsers, setRecentUsers] = useState([]);
  const [recentProducts, setRecentProducts] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [userGrowth, setUserGrowth] = useState([]);
  const [orderStats, setOrderStats] = useState([]);

  useEffect(() => {
    const ud = localStorage.getItem("user");
    if (ud) {
      try {
        const u = JSON.parse(ud);
        setUser(u);
        if (u.role !== "admin") navigate("/");
        else fetchDashboardData();
      } catch {
        setLoading(false);
      }
    } else navigate("/login");
  }, [navigate]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [
        statsRes,
        usersRes,
        productsRes,
        ordersRes,
        growthRes,
        orderStatsRes,
      ] = await Promise.all([
        api.get("/admin/dashboard"),
        api.get("/admin/users?limit=5").catch(() => ({ data: [] })),
        api
          .get("/admin/products?limit=5&sort=createdAt")
          .catch(() => ({ data: [] })),
        api.get("/orders?limit=5").catch(() => ({ data: [] })),
        api.get("/admin/analytics/user-growth").catch(() => ({ data: [] })),
        api.get("/admin/analytics/orders").catch(() => ({ data: [] })),
      ]);
      if (statsRes.data) setStats((prev) => ({ ...prev, ...statsRes.data }));
      setRecentUsers((usersRes.data || []).slice(0, 5));
      setRecentProducts((productsRes.data || []).slice(0, 5));
      setRecentOrders((ordersRes.data || []).slice(0, 5));
      setUserGrowth(growthRes.data || []);
      setOrderStats(orderStatsRes.data || []);
    } catch (err) {
      console.error("Dashboard fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveUser = async (userId) => {
    try {
      await api.put(`/users/${userId}/verify`);
      fetchDashboardData();
    } catch {
      showToast("Failed to approve user", "error");
    }
  };

  const orderStatusPie = useMemo(() => {
    if (!stats.totalOrders) return [];
    const result = [];
    if (stats.activeOrdersToday > 0)
      result.push({ name: "Active", value: stats.activeOrdersToday });
    if (stats.completedOrders > 0)
      result.push({ name: "Completed", value: stats.completedOrders });
    if (stats.cancelledOrders > 0)
      result.push({ name: "Cancelled", value: stats.cancelledOrders });
    const other =
      stats.totalOrders -
      (stats.activeOrdersToday +
        stats.completedOrders +
        (stats.cancelledOrders || 0));
    if (other > 0) result.push({ name: "Other", value: other });
    return result;
  }, [stats]);

  if (loading) {
    return (
      <AdminLayout>
        <div className="ad-loading">
          <div className="ad-dots">
            <span />
            <span />
            <span />
          </div>
          <p>{t("adminDashboard.loading")}</p>
        </div>
      </AdminLayout>
    );
  }

  const kpis = [
    {
      icon: <FaUsers />,
      label: t("adminDashboard.totalUsers") || "Total Users",
      value: stats.totalUsers,
      cls: "users",
    },
    {
      icon: <RoleIllustration role="farmer" size="lg" />,
      label: t("adminDashboard.totalFarmers"),
      value: stats.totalFarmers,
      cls: "farmers",
    },
    {
      icon: <RoleIllustration role="consumer" size="lg" />,
      label: t("adminDashboard.totalConsumers"),
      value: stats.totalConsumers,
      cls: "consumers",
    },
    {
      icon: <FaBox />,
      label: t("adminDashboard.totalProducts"),
      value: stats.totalProducts,
      cls: "products",
    },
    {
      icon: <FaClipboardList />,
      label: t("adminDashboard.activeOrdersToday"),
      value: stats.activeOrdersToday,
      cls: "active",
    },
    {
      icon: <FaCheckCircle />,
      label: t("adminDashboard.completedOrders"),
      value: stats.completedOrders,
      cls: "completed",
    },
    {
      icon: <FaDollarSign />,
      label: "Total Revenue",
      value: `Rs. ${toLocalizedPrice(stats.totalRevenue)}`,
      cls: "revenue",
      isText: true,
    },
    {
      icon: <FaShieldAlt />,
      label: "Pending Verifications",
      value: stats.pendingVerifications,
      cls: "verify",
      alert: stats.pendingVerifications > 0,
    },
  ];

  return (
    <AdminLayout>
      <div className="ad-content">
        {/* Header */}
        <div className="ad-head">
          <div>
            <h1 className="ad-title">{t("adminDashboard.title")}</h1>
            <p className="ad-subtitle">
              {t("adminDashboard.welcome", { name: user?.name || "Admin" })}
            </p>
          </div>
          <div className="ad-head-date">
            {new Date().toLocaleDateString("en-US", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </div>
        </div>

        {/* KPI Grid */}
        <div className="ad-kpis">
          {kpis.map((k) => (
            <div
              key={k.cls}
              className={`ad-kpi ${k.alert ? "ad-kpi--alert" : ""}`}
            >
              <div className={`ad-kpi-ico ad-kpi-ico--${k.cls}`}>{k.icon}</div>
              <div className="ad-kpi-body">
                <span className="ad-kpi-val">
                  {k.isText ? k.value : toLocalizedNumber(k.value)}
                </span>
                <span className="ad-kpi-lbl">{k.label}</span>
                {k.sub && <span className="ad-kpi-sub">{k.sub}</span>}
              </div>
            </div>
          ))}
        </div>

        {/* Charts Row */}
        <div className="ad-charts-row">
          <div className="ad-card ad-card--wide">
            <h3 className="ad-card-title">{t("adminDashboard.userGrowth")}</h3>
            {userGrowth.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={userGrowth}>
                  <defs>
                    <linearGradient id="gFarmers" x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="0%"
                        stopColor="#22c55e"
                        stopOpacity={0.35}
                      />
                      <stop
                        offset="100%"
                        stopColor="#22c55e"
                        stopOpacity={0.02}
                      />
                    </linearGradient>
                    <linearGradient id="gConsumers" x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="0%"
                        stopColor="#3b82f6"
                        stopOpacity={0.35}
                      />
                      <stop
                        offset="100%"
                        stopColor="#3b82f6"
                        stopOpacity={0.02}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" stroke="#aaa" fontSize={12} />
                  <YAxis stroke="#aaa" fontSize={12} />
                  <Tooltip />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="farmers"
                    stroke="#22c55e"
                    fill="url(#gFarmers)"
                    strokeWidth={2.5}
                    name="Farmers"
                  />
                  <Area
                    type="monotone"
                    dataKey="consumers"
                    stroke="#3b82f6"
                    fill="url(#gConsumers)"
                    strokeWidth={2.5}
                    name="Consumers"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="ad-empty-chart">{t("adminDashboard.noData")}</div>
            )}
          </div>

          <div className="ad-card">
            <h3 className="ad-card-title">Order Breakdown</h3>
            {orderStatusPie.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={orderStatusPie}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={90}
                    paddingAngle={3}
                    dataKey="value"
                    label={({ name, percent }) =>
                      `${name} ${(percent * 100).toFixed(0)}%`
                    }
                    labelLine={false}
                  >
                    {orderStatusPie.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="ad-empty-chart">{t("adminDashboard.noData")}</div>
            )}
          </div>
        </div>

        {/* Orders Week Chart */}
        <div className="ad-card">
          <h3 className="ad-card-title">
            {t("adminDashboard.ordersOverview")} — Last 7 Days
          </h3>
          {orderStats.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={orderStats} barGap={4}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="day" stroke="#aaa" fontSize={12} />
                <YAxis stroke="#aaa" fontSize={12} allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar
                  dataKey="completed"
                  fill="#22c55e"
                  radius={[4, 4, 0, 0]}
                  name="Completed"
                  barSize={20}
                />
                <Bar
                  dataKey="pending"
                  fill="#f59e0b"
                  radius={[4, 4, 0, 0]}
                  name="Pending"
                  barSize={20}
                />
                <Bar
                  dataKey="rejected"
                  fill="#ef4444"
                  radius={[4, 4, 0, 0]}
                  name="Cancelled"
                  barSize={20}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="ad-empty-chart">{t("adminDashboard.noData")}</div>
          )}
        </div>

        {/* Recent Users */}
        <div className="ad-card">
          <div className="ad-card-head">
            <h3 className="ad-card-title">{t("adminDashboard.recentUsers")}</h3>
            <Link to="/admin/users" className="ad-view-all">
              {t("adminDashboard.viewAll")} <FaArrowRight />
            </Link>
          </div>
          <div className="ad-tbl-wrap">
            <table className="ad-tbl">
              <thead>
                <tr>
                  <th>{t("adminDashboard.name")}</th>
                  <th>{t("adminDashboard.role")}</th>
                  <th>{t("adminDashboard.dateJoined")}</th>
                  <th>{t("adminDashboard.status")}</th>
                </tr>
              </thead>
              <tbody>
                {recentUsers.length > 0 ? (
                  recentUsers.map((u) => (
                    <tr key={u._id}>
                      <td className="ad-tbl-name">
                        <div className="ad-tbl-avatar">
                          {u.name?.charAt(0).toUpperCase()}
                        </div>
                        {u.name}
                      </td>
                      <td>
                        <span className={`ad-badge ad-badge--${u.role}`}>
                          {u.role === "farmer" ? (
                            <>
                              <RoleIllustration role="farmer" size="sm" />{" "}
                              Farmer
                            </>
                          ) : (
                            <>
                              <RoleIllustration role="consumer" size="sm" />{" "}
                              Consumer
                            </>
                          )}
                        </span>
                      </td>
                      <td className="ad-tbl-date">
                        {new Date(u.createdAt).toLocaleDateString()}
                      </td>
                      <td>
                        <span
                          className={`ad-pill ${u.verified ? "ad-pill--green" : "ad-pill--amber"}`}
                        >
                          {u.verified
                            ? t("adminDashboard.active")
                            : t("adminDashboard.pending")}
                        </span>
                      </td>
                      <td className="ad-tbl-actions">
                        {!u.verified && u.role === "farmer" && (
                          <button
                            className="ad-btn ad-btn--green"
                            onClick={() => handleApproveUser(u._id)}
                          >
                            {t("adminDashboard.approve")}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="ad-tbl-empty">
                      {t("adminDashboard.noRecentUsers")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent Products */}
        <div className="ad-card">
          <div className="ad-card-head">
            <h3 className="ad-card-title">
              {t("adminDashboard.recentProducts")}
            </h3>
            <Link to="/admin/products" className="ad-view-all">
              {t("adminDashboard.viewAll")} <FaArrowRight />
            </Link>
          </div>
          <div className="ad-tbl-wrap">
            <table className="ad-tbl">
              <thead>
                <tr>
                  <th>{t("adminDashboard.image")}</th>
                  <th>{t("adminDashboard.productName")}</th>
                  <th>{t("adminDashboard.farmer")}</th>
                  <th>{t("adminDashboard.category")}</th>
                  <th>{t("adminDashboard.dateAdded")}</th>
                  <th>{t("adminDashboard.status")}</th>
                  <th>{t("adminDashboard.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {recentProducts.length > 0 ? (
                  recentProducts.map((p) => (
                    <tr key={p._id}>
                      <td>
                        <img
                          src={
                            p.images?.length > 0
                              ? getImageUrl(p.images[0])
                              : "/images/default-product.png"
                          }
                          alt={p.name}
                          className="ad-tbl-thumb"
                        />
                      </td>
                      <td className="ad-tbl-bold">{p.name}</td>
                      <td>{p.farmer?.name || "N/A"}</td>
                      <td>{p.category || "N/A"}</td>
                      <td className="ad-tbl-date">
                        {new Date(p.createdAt).toLocaleDateString()}
                      </td>
                      <td>
                        <span
                          className={`ad-pill ${p.isAvailable ? "ad-pill--green" : "ad-pill--amber"}`}
                        >
                          {p.isAvailable
                            ? t("adminDashboard.active")
                            : "Inactive"}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="ad-tbl-empty">
                      {t("adminDashboard.noRecentProducts")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent Orders */}
        <div className="ad-card">
          <div className="ad-card-head">
            <h3 className="ad-card-title">
              {t("adminDashboard.recentOrders")}
            </h3>
            <Link to="/admin/orders" className="ad-view-all">
              {t("adminDashboard.viewAll")} <FaArrowRight />
            </Link>
          </div>
          <div className="ad-tbl-wrap">
            <table className="ad-tbl">
              <thead>
                <tr>
                  <th>{t("adminDashboard.orderId")}</th>
                  <th>{t("adminDashboard.consumer")}</th>
                  <th>{t("adminDashboard.farmer")}</th>
                  <th>{t("adminDashboard.totalPrice")}</th>
                  <th>{t("adminDashboard.status")}</th>
                  <th>{t("adminDashboard.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {recentOrders.length > 0 ? (
                  recentOrders.map((o) => (
                    <tr key={o._id}>
                      <td className="ad-tbl-id">#{o._id.slice(-8)}</td>
                      <td>{o.consumer?.name || "N/A"}</td>
                      <td>{o.farmer?.name || "N/A"}</td>
                      <td className="ad-tbl-bold">
                        Rs. {toLocalizedPrice(o.totalPrice || 0)}
                      </td>
                      <td>
                        <span
                          className={`ad-pill ad-pill--${(o.orderStatus || "pending").toLowerCase()}`}
                        >
                          {toLocalizedStatus(o.orderStatus || "pending", t)}
                        </span>
                      </td>
                      <td>
                        <Link
                          to={`/admin/orders/${o._id}`}
                          className="ad-btn ad-btn--blue"
                        >
                          {t("adminDashboard.viewDetails")}
                        </Link>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="ad-tbl-empty">
                      {t("adminDashboard.noRecentOrders")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
